import { Dir, type Direction } from './direction';

/**
 * Tile behaviors the sim understands. Content maps many visual tile types onto these (a rock, a
 * tree and a gnome are all `Obstacle`), so the sim stays small while levels stay varied.
 */
export const Tile = {
  /** Drivable, nothing to mow (paths, driveways, patios, cleared brush). */
  Ground: 0,
  /** Unmowed grass: the objective. */
  Grass: 1,
  /** Grass that has been mowed; carries a stripe direction. */
  Mowed: 2,
  /** Crashes every mower. */
  Obstacle: 3,
  /** Must be mowed; takes more than one pass (see {@link grassHeight}). */
  TallGrass: 4,
  /** Sticks/brush: crashes weak mowers; strong mowers plow through and clear it. */
  Brush: 5,
  /** Drivable but trampling it is penalized. Not mowed. */
  FlowerBed: 6,
  /** Drivable; slows the mower while crossing. */
  Mud: 7,
  /** Crashes every mower like an obstacle, but a tool can trim it away (shrubs, hedgerows). */
  Hedge: 8,
  /** Tall grass cut down once by a weak bite: still tall-ish, one step above ordinary grass. */
  RoughGrass: 9,
  /**
   * A lawn decoration (gnomes, bird baths): the mower can drive through it, but smashes it to the
   * ground. No hit, but damaged property costs coins.
   */
  Ornament: 10,
  /**
   * A garden hose across the lawn: drivable, nothing to mow, but the mower chews it (coins). Once
   * chewed it becomes {@link Tile.HoseChewed}, which costs nothing more.
   */
  Hose: 11,
  /** A hose the mower already chewed. */
  HoseChewed: 12,
  /**
   * A sprinkler head: drivable, nothing to mow, but the mower breaks it (coins). A working head
   * sprays the 8 tiles around it on a timer; wet tiles are slippery (no turning).
   */
  Sprinkler: 13,
  /**
   * A molehill a mole pushed up through the lawn: drivable, and it must be flattened (mowed over
   * once) before the lawn counts as done.
   */
  Molehill: 14,
  /**
   * A thorn patch (brambles, thistles): drivable and nothing to mow, but driving over it gives the
   * mower a flat tire (slow until patched), unless Hardened Tires take the puncture.
   */
  Thorns: 15,
  /**
   * A beehive (Rolling Meadows): any mower can drive through it (break tier 0), smashing it, but
   * the swarm stings a heart away and the beekeeper's loss costs coins. Solid to critters.
   */
  Hive: 16,
} as const;
export type Tile = (typeof Tile)[keyof typeof Tile];

const TILE_COUNT = 17;

export function isTile(value: unknown): value is Tile {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 && value < TILE_COUNT;
}

/** Solid for every mower and critter: obstacles and hedges (and the fence around the map). */
export function isSolid(tile: Tile): boolean {
  return tile === Tile.Obstacle || tile === Tile.Hedge || tile === Tile.Hive;
}

/**
 * What the string trimmer swings over to the grass beyond (owner, 2026-10-23: grass boxed in by
 * a hose and flowers must not need the mower to drive over them): anything solid, and anything a
 * mower would damage (flowers, ornaments, a hose, a sprinkler).
 */
export function trimmerReachesOver(tile: Tile): boolean {
  return (
    isSolid(tile) ||
    tile === Tile.FlowerBed ||
    tile === Tile.Ornament ||
    tile === Tile.Hose ||
    tile === Tile.Sprinkler
  );
}

/** Tiles that count toward "mow everything". */
export function needsMowing(tile: Tile): boolean {
  return grassHeight(tile) > 0;
}

/**
 * How much cutting a tile still needs. Each pass takes the mower's bite off (reel 1, engine mowers
 * 2, +1 with Sharpened Blades); at 0 it is mowed. So tall grass takes a stock reel three passes, a
 * stock engine mower two, and one only with sharpened blades on an engine mower.
 */
export function grassHeight(tile: Tile): number {
  switch (tile) {
    case Tile.TallGrass:
      return 3;
    case Tile.RoughGrass:
      return 2;
    case Tile.Grass:
    case Tile.Molehill:
      return 1;
    default:
      return 0;
  }
}

const GRASS_BY_HEIGHT: readonly Tile[] = [Tile.Mowed, Tile.Grass, Tile.RoughGrass, Tile.TallGrass];

/** Passes a mower with this bite needs to mow tall grass. */
export function tallGrassPasses(bite: number): number {
  return Math.ceil(grassHeight(Tile.TallGrass) / bite);
}

/** Grass taller than ordinary (tall or half-cut): heavy going for a mower. */
export function isTallGrass(tile: Tile): boolean {
  return grassHeight(tile) > 1;
}

/** Pushing into tall grass multiplies the time to cross a tile by this ratio. */
export const TALL_GRASS_SLOWDOWN = { num: 5, den: 4 } as const;
/** Extra fuel a pass that cuts tall grass burns (on top of the usual 1 per tile). */
export const TALL_GRASS_EXTRA_FUEL = 1;
/** Cut power needed to drive through (and clear) brush instead of crashing. */
export const BRUSH_CUT_POWER = 3;
/**
 * Break tiers: a lawn item with a tier (0 cone ... 4 ball washer) is smashed by a mower whose
 * break power reaches it and crashes any weaker one. From this tier up, plowing through one is
 * heavy going: crossing its tile takes this much longer.
 */
export const HEAVY_BREAK_TIER = 3;
export const HEAVY_BREAK_SLOWDOWN = { num: 3, den: 2 } as const;
/** Mud multiplies the time to cross a tile by this ratio. */
export const MUD_SLOWDOWN = { num: 3, den: 2 } as const;
/** A flat tire multiplies the time to cross a tile by this ratio (about half speed). */
export const FLAT_SLOWDOWN = { num: 2, den: 1 } as const;
/** Hills: going uphill multiplies the time to cross a tile by this, downhill by `DOWNHILL`. */
export const UPHILL = { num: 3, den: 2 } as const;
export const DOWNHILL = { num: 3, den: 4 } as const;
/** Extra fuel a tile driven uphill burns (on top of the usual 1). */
export const UPHILL_EXTRA_FUEL = 1;

/** What mowing a tile did. */
export type MowResult =
  'cut' | 'thinned' | 'restriped' | 'cleared' | 'smashed' | 'chewed' | 'broken' | 'none';

/**
 * A cell outside the lawn's rectangle (a secret yard and the gate into it): driven, smashed and
 * collected on like any tile, but never part of the lawn (nothing there needs mowing).
 */
export interface ExtraCell {
  x: number;
  y: number;
  tile: Tile;
  /** Break tier of the item standing there (the gate), or absent. */
  toughness?: number;
}

/** Map key for an extra cell (lawns are far smaller than the offset). */
const extraKey = (x: number, y: number): number => (y + 4096) * 8192 + (x + 4096);

/**
 * Fixed-size tile grid. Everything outside the bounds behaves as an obstacle (the fence), except
 * the few extra cells a secret yard adds beyond it.
 */
export class Grid {
  readonly width: number;
  readonly height: number;
  readonly totalGrass: number;
  private readonly tiles: Uint8Array;
  private readonly stripes: Uint8Array;
  /** Break tier per tile, -1 where nothing breakable stands. */
  private readonly toughness: Int8Array;
  private grassLeft: number;
  /** Cells beyond the lawn (a secret yard): tile and break tier. */
  private readonly extras = new Map<number, { tile: Tile; toughness: number }>();

  constructor(
    width: number,
    height: number,
    tiles: ArrayLike<Tile>,
    toughness?: ArrayLike<number>,
    extras?: readonly ExtraCell[],
  ) {
    if (!Number.isInteger(width) || !Number.isInteger(height) || width <= 0 || height <= 0) {
      throw new RangeError(`Grid: invalid size ${width}x${height}`);
    }
    if (tiles.length !== width * height) {
      throw new RangeError(`Grid: expected ${width * height} tiles, got ${tiles.length}`);
    }
    this.width = width;
    this.height = height;
    this.tiles = Uint8Array.from(tiles);
    this.stripes = new Uint8Array(width * height).fill(Dir.N);
    if (toughness && toughness.length !== width * height) {
      throw new RangeError(`Grid: expected ${width * height} toughness values`);
    }
    this.toughness = toughness ? Int8Array.from(toughness) : new Int8Array(width * height).fill(-1);
    let grass = 0;
    for (const t of this.tiles) {
      if (!isTile(t)) throw new RangeError(`Grid: unknown tile value ${t}`);
      if (t === Tile.Mowed) throw new RangeError('Grid: levels must not start with mowed tiles');
      if (needsMowing(t)) grass++;
    }
    this.totalGrass = grass;
    this.grassLeft = grass;
    for (const c of extras ?? []) {
      if (this.inBounds(c.x, c.y)) throw new RangeError('Grid: extra cells lie outside the lawn');
      if (needsMowing(c.tile)) throw new RangeError('Grid: nothing beyond the lawn needs mowing');
      this.extras.set(extraKey(c.x, c.y), { tile: c.tile, toughness: c.toughness ?? -1 });
    }
  }

  inBounds(x: number, y: number): boolean {
    return x >= 0 && y >= 0 && x < this.width && y < this.height;
  }

  /** On the lawn or one of the extra cells beyond it (somewhere the mower can be). */
  inPlay(x: number, y: number): boolean {
    return this.inBounds(x, y) || this.extras.has(extraKey(x, y));
  }

  tileAt(x: number, y: number): Tile {
    if (!this.inBounds(x, y)) return this.extras.get(extraKey(x, y))?.tile ?? Tile.Obstacle;
    return this.tiles[y * this.width + x] as Tile;
  }

  /** Blocks every mower (obstacles and the fence). */
  isBlocked(x: number, y: number): boolean {
    return isSolid(this.tileAt(x, y));
  }

  /** The break tier of the lawn item standing at (x, y), or -1 (nothing breakable). */
  toughnessAt(x: number, y: number): number {
    if (!this.inBounds(x, y)) return this.extras.get(extraKey(x, y))?.toughness ?? -1;
    return this.toughness[y * this.width + x]!;
  }

  /**
   * Blocks a mower with the given cut power and break power: a breakable item only stops a mower
   * too weak to smash it. Leaving `breakPower` out treats every breakable as a wall (the planners
   * and level checks: a lawn never needs anything broken).
   */
  isBlockedFor(x: number, y: number, cutPower: number, breakPower = -1): boolean {
    const tier = this.toughnessAt(x, y);
    if (tier >= 0) return breakPower < tier;
    const t = this.tileAt(x, y);
    return isSolid(t) || (t === Tile.Brush && cutPower < BRUSH_CUT_POWER);
  }

  /** Smashes the breakable item at (x, y) to the ground; returns its tier (-1: nothing there). */
  smash(x: number, y: number): number {
    const tier = this.toughnessAt(x, y);
    if (tier < 0) return -1;
    if (!this.inBounds(x, y)) {
      this.extras.set(extraKey(x, y), { tile: Tile.Ground, toughness: -1 });
      return tier;
    }
    const i = y * this.width + x;
    this.tiles[i] = Tile.Ground;
    this.toughness[i] = -1;
    return tier;
  }

  /** Ticks needed to drive from this tile's center to the next one. */
  ticksToCross(x: number, y: number, ticksPerTile: number): number {
    if (this.tileAt(x, y) !== Tile.Mud) return ticksPerTile;
    return Math.ceil((ticksPerTile * MUD_SLOWDOWN.num) / MUD_SLOWDOWN.den);
  }

  /** Direction the tile was last mowed in (meaningful only for mowed tiles). */
  stripeAt(x: number, y: number): Direction {
    if (!this.inBounds(x, y)) return Dir.N;
    return this.stripes[y * this.width + x] as Direction;
  }

  /**
   * Mows the tile with a mower heading `dir`: grass loses `bite` of height (see
   * {@link grassHeight}); brush is plowed through with enough cut power.
   */
  mow(x: number, y: number, dir: Direction, cutPower: number, bite: number): MowResult {
    if (!this.inBounds(x, y)) {
      // Beyond the lawn only a pile of sticks can be plowed through (a secret yard's gate).
      const cell = this.extras.get(extraKey(x, y));
      if (cell?.tile !== Tile.Brush || cutPower < BRUSH_CUT_POWER) return 'none';
      cell.tile = Tile.Ground;
      return 'cleared';
    }
    const i = y * this.width + x;
    switch (this.tiles[i] as Tile) {
      case Tile.Grass:
      case Tile.Molehill:
      case Tile.RoughGrass:
      case Tile.TallGrass: {
        const left = grassHeight(this.tiles[i] as Tile) - bite;
        if (left > 0) {
          this.tiles[i] = GRASS_BY_HEIGHT[left] as Tile;
          return 'thinned';
        }
        this.tiles[i] = Tile.Mowed;
        this.stripes[i] = dir;
        this.grassLeft--;
        return 'cut';
      }
      case Tile.Mowed:
        this.stripes[i] = dir;
        return 'restriped';
      case Tile.Brush:
        if (cutPower < BRUSH_CUT_POWER) return 'none';
        this.tiles[i] = Tile.Ground;
        return 'cleared';
      case Tile.Ornament:
        this.tiles[i] = Tile.Ground;
        return 'smashed';
      case Tile.Hose:
        this.tiles[i] = Tile.HoseChewed;
        return 'chewed';
      case Tile.Sprinkler:
        this.tiles[i] = Tile.Ground;
        return 'broken';
      default:
        return 'none';
    }
  }

  /** Meadow grass grows back on a mowed tile (there is mowing to do again). */
  regrow(x: number, y: number): boolean {
    if (this.tileAt(x, y) !== Tile.Mowed) return false;
    this.tiles[y * this.width + x] = Tile.Grass;
    this.grassLeft++;
    return true;
  }

  /** A mole pushes up a molehill through mowed lawn (there is mowing to do again). */
  raiseMolehill(x: number, y: number): boolean {
    if (this.tileAt(x, y) !== Tile.Mowed) return false;
    this.tiles[y * this.width + x] = Tile.Molehill;
    this.grassLeft++;
    return true;
  }

  /**
   * A fallen tree's log lands here: the tile becomes an obstacle (any grass under it no longer
   * needs mowing). Returns false outside the lawn.
   */
  bury(x: number, y: number): boolean {
    if (!this.inBounds(x, y)) return false;
    if (needsMowing(this.tileAt(x, y))) this.grassLeft--;
    this.tiles[y * this.width + x] = Tile.Obstacle;
    this.toughness[y * this.width + x] = -1;
    return true;
  }

  /** A fallen tree leaves its stump: drivable ground. */
  topple(x: number, y: number): void {
    if (this.tileAt(x, y) === Tile.Obstacle) this.tiles[y * this.width + x] = Tile.Ground;
  }

  /** Whether (x, y) is within reach of a working sprinkler's spray (one of the 8 tiles around it). */
  inSprinklerReach(x: number, y: number): boolean {
    for (let oy = -1; oy <= 1; oy++) {
      for (let ox = -1; ox <= 1; ox++) {
        if ((ox !== 0 || oy !== 0) && this.tileAt(x + ox, y + oy) === Tile.Sprinkler) return true;
      }
    }
    return false;
  }

  /** Clears a tile of one of the given kinds to ground (tools). Returns true if it did. */
  clear(x: number, y: number, kinds: readonly Tile[]): boolean {
    if (!this.inBounds(x, y)) return false;
    const i = y * this.width + x;
    const t = this.tiles[i] as Tile;
    if (!kinds.includes(t) || needsMowing(t) || t === Tile.Mowed) return false;
    this.tiles[i] = Tile.Ground;
    this.toughness[i] = -1;
    return true;
  }

  get grassRemaining(): number {
    return this.grassLeft;
  }

  /** Whole-number percentage mowed (100 only when every blade is cut). */
  get percentMowed(): number {
    if (this.totalGrass === 0) return 100;
    return Math.floor((100 * (this.totalGrass - this.grassLeft)) / this.totalGrass);
  }

  /** Raw views for hashing and rendering. Do not mutate. */
  rawTiles(): Readonly<Uint8Array> {
    return this.tiles;
  }

  rawStripes(): Readonly<Uint8Array> {
    return this.stripes;
  }
}
